import React from 'react';

import gridStyles from 'assets/style/grid.module.css';
import clsx from 'clsx';

import treatmentToBlock from 'js/components/InfoColumnBlocks/transform/treatment';
import questionToBlock from 'js/components/InfoColumnBlocks/transform/question';
import inspirationArticleToBlock from 'js/components/InfoColumnBlocks/transform/inspiration-article';
import InfoColumnBlocks from 'js/components/InfoColumnBlocks';
import { InfoColumnLinks } from 'js/components/InfoColumnLinks/InfoColumnLinks';
import { Aliases } from 'js/components/Aliases';
import { Breadcrumbs } from 'js/components/Breadcrumbs/Breadcrumbs';
import { BackgroundImage } from 'js/components/generic/BackgroundImage';
import { Divider } from 'js/components/generic/Divider';
import ContentSection from 'js/pages/TreatmentPage/ContentSection';
import TopCities from 'js/components/TopCities';
import { VenueSearch } from 'js/components/VenueSearch';
import { Markdown } from 'js/components/generic/Markdown';
import { QuestionsAndAnswers } from 'js/components/QuestionsAndAnswers';
import InfoBlock from 'js/components/InfoColumnBlocks/InfoBlock';
import { SocialSharing } from 'js/components/SocialSharing/SocialSharing';
import { Scripts as SocialSharingScripts } from 'js/components/SocialSharing/Scripts';
import { useForceUpdate } from 'js/hooks/useForceUpdate';
import partial from 'lodash.partial';
import { substituteWithDictionary } from 'js/helpers/i18n';
import { trackStructuredEvent } from 'js/helpers/google-tag-manager';
import { useTrackPageAndSessionView } from 'js/hooks/useTrackPageAndSessionView';
import styles from './TreatmentPage.module.css';
import { Context } from '../../components/LocaleWrapper';

const CONTENT_HEIGHT = 800;
const generateTreatmentUri = (generateUri, block) =>
  generateUri('treatment', {
    normalisedName: block.normalisedName,
  });
const orderTreatmentImages = (container) => ({
  default: container.default,
  tablet: container.tablet,
  desktop: container.desktop,
});

export default function TreatmentPage() {
  const descriptionContent = React.useRef(null);
  const context = React.useContext(Context);
  const forceUpdate = useForceUpdate();
  useTrackPageAndSessionView();

  React.useEffect(() => {
    function onWindowResize() {
      forceUpdate();
    }

    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);

  function addBottomVenueSearch() {
    if (!descriptionContent.current) {
      return false;
    }
    return descriptionContent.current.offsetHeight > CONTENT_HEIGHT;
  }

  function getBreadcrumbs(treatment, treatmentType) {
    const params = [
      {
        name: context.i18n('page.treatment-guide.title'),
        pageType: 'treatmentGuide',
      },
      {
        name: treatmentType.name,
        pageType: 'treatmentType',
        uriParams: {
          treatmentTypeId: treatmentType.id,
        },
      },
      {
        name: treatment.name,
      },
    ];
    return <Breadcrumbs breadcrumbsParams={params} showIcon />;
  }

  async function handleBestForBrowseLinkClick(event) {
    event.persist();
    event.preventDefault();

    await trackStructuredEvent({
      category: 'treatment_guide',
      property: 'btt_link',
      action: 'click',
      label: context.pageData.treatment.bestForBrowsePages[0].treatments[0].id,
      value: context.pageData.treatment.bestForBrowsePages.length,
    });

    window.location.assign(event.target.href);
  }

  const {
    i18n,
    generateUri,
    pageData: { treatment: pageData, popularLocations },
  } = context;

  const treatment = pageData.treatment;
  const treatmentId = treatment.id;
  const treatmentNormalisedName = treatment.normalisedName;
  const treatmentCms = i18n(`page.treatment.${treatmentId}`);
  const treatmentType = pageData.treatmentType;
  const relatedTreatments = treatmentToBlock(
    pageData.relatedTreatments || [],
    generateUri,
    i18n
  );
  const inspirationArticles =
    inspirationArticleToBlock(pageData.treatmentInspirationArticles || []) ||
    [];
  const otherTreatmentsTitle = i18n(
    'page.treatment.section-other-treatments',
    treatmentType.name.toLowerCase()
  );
  const otherTreatments = pageData.otherTreatments;
  const breadcrumbs = getBreadcrumbs(treatment, treatmentType);
  const treatmentImages = treatmentCms.image || {};
  const questionsTitle = i18n(
    'page.treatment.section-questions-and-answers',
    treatment.name
  );
  const questions = questionToBlock(pageData.questionsAndAnswers || []);
  const browseUriValues = {
    treatments: treatmentNormalisedName,
    offerResultType: 'LOCAL',
  };

  const venueSearch = (
    <VenueSearch
      title={i18n('venue-search.title').replace(
        '{entity-type}',
        treatment.name
      )}
      typeAhead={{ treatmentIds: treatment.id }}
      browseUriValues={browseUriValues}
      popularLocations={popularLocations}
    >
      <TopCities
        title={i18n('top-cities.title')}
        showMore={i18n('top-cities.show-more')}
        browseUriValues={browseUriValues}
      />
    </VenueSearch>
  );

  const contentVenueSearch = addBottomVenueSearch() ? (
    <VenueSearch
      title={i18n('venue-search.title').replace(
        '{entity-type}',
        treatment.name
      )}
      typeAhead={{ treatmentIds: treatment.id }}
      browseUriValues={browseUriValues}
      popularLocations={popularLocations}
    />
  ) : null;

  const bestForBrowsePagesHeading = substituteWithDictionary(
    i18n('page.treatment.best-for-browse-pages-heading'),
    { treatmentName: treatment.name }
  );

  const bestForBrowsePages = context.pageData.treatment.bestForBrowsePages;

  return (
    <div className={styles.treatment}>
      <div className={styles.header}>
        <ContentSection>
          <div className={gridStyles['col-xs-12']}>
            {breadcrumbs}
            <h1 className={styles.title}>{treatment.name}</h1>
            <div className={styles['aliases-wrapper']}>
              <Aliases aliases={treatment.aliases} />
            </div>
          </div>
        </ContentSection>
        <ContentSection style={{ position: 'relative' }}>
          <div className={gridStyles['col-md-8']}>
            <div className={styles.image}>
              <BackgroundImage images={orderTreatmentImages(treatmentImages)} />
            </div>
          </div>
        </ContentSection>
      </div>
      <div className={styles['section-content']} ref={descriptionContent}>
        <ContentSection>
          <div
            className={clsx(gridStyles['col-xs-12'], gridStyles['col-md-8'])}
          >
            <div
              className={clsx(
                styles.venueSearch,
                gridStyles['col-xs-12'],
                gridStyles['col-sm-6']
              )}
            >
              {venueSearch}
            </div>

            <div className={styles.description}>{treatmentCms.description}</div>
            <Divider inContent />
            <Markdown>{treatmentCms['long-description']}</Markdown>

            <Divider inContent footnote />
          </div>
        </ContentSection>
      </div>

      <ContentSection>
        <div
          className={clsx(
            gridStyles['col-xs-12'],
            gridStyles['col-sm-6'],
            gridStyles['col-md-12'],
            gridStyles['col-lg-12']
          )}
        >
          <SocialSharing />
        </div>

        <div
          className={clsx(
            gridStyles['col-xs-12'],
            gridStyles['col-sm-6'],
            gridStyles['col-md-12'],
            gridStyles['col-lg-12']
          )}
        >
          <div className={gridStyles.row}>
            <div className={clsx(gridStyles['col-md-8'])} />
            <div
              className={clsx(
                styles.contentSearch,
                gridStyles['col-xs-12'],
                gridStyles['col-sm-12'],
                gridStyles['col-md-4']
              )}
            >
              {contentVenueSearch}
            </div>
          </div>
        </div>
      </ContentSection>

      {inspirationArticles.length === 0 ? null : (
        <ContentSection>
          <div className={styles.contentBlocks}>
            <InfoColumnBlocks
              title={i18n('page.treatment.section-inspiration-articles')}
              blocks={inspirationArticles}
              contentBlock={InfoBlock}
            />
          </div>
        </ContentSection>
      )}

      <ContentSection>
        <div className={styles.contentBlocks}>
          <InfoColumnBlocks
            title={i18n('page.treatment.section-related-treatments')}
            blocks={relatedTreatments}
            contentBlock={InfoBlock}
          />
        </div>
      </ContentSection>

      {bestForBrowsePages && Boolean(bestForBrowsePages.length) && (
        <ContentSection>
          <h2 className={styles.bestForBrowsePagesHeading}>
            {bestForBrowsePagesHeading}
          </h2>

          <ul className={styles.bestForBrowsePagesLinks}>
            {bestForBrowsePages.map((bestForBrowsePage) => (
              <li key={bestForBrowsePage.treatments[0].uri}>
                <a
                  href={bestForBrowsePage.treatments[0].uri}
                  onClick={handleBestForBrowseLinkClick}
                >
                  {bestForBrowsePage.location}
                </a>
              </li>
            ))}
          </ul>
        </ContentSection>
      )}

      <ContentSection>
        <div className={styles.contentBlocks}>
          <InfoColumnLinks
            url={partial(generateTreatmentUri, generateUri)}
            title={otherTreatmentsTitle}
            links={otherTreatments}
          />
        </div>
      </ContentSection>
      <ContentSection>
        <div className={styles.contentBlocks} id="questions">
          <InfoColumnBlocks
            title={questionsTitle}
            blocks={questions}
            contentBlock={QuestionsAndAnswers}
          />
        </div>
      </ContentSection>

      <SocialSharingScripts />
    </div>
  );
}
